import './perks-join.component.scss';

import { h } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { useLocation } from 'wouter-preact';

import { LegacyAuthModalState } from '../../../auth/models/auth.model';
import { authenticationFlowService } from '../../../auth/services/authentication-flow.service';
import {
  selectAccountSettings,
  selectAccountSettingsLoading,
  selectIsAuthenticated,
} from '../../../auth/store/auth.selectors';
import { SpinnerComponent } from '../../../common/components/spinner/spinner.component';
import { useTranslation } from '../../../i18n';
import { MembershipManagement } from '../../../membership/components/membership-management/membership-management.component';
import {
  selectMemberships,
  selectMembershipsLoading,
} from '../../../membership/store/membership.selectors';
import type { AccountSettingsResponse } from '../../../models/common.model';
import { useAppSelector } from '../../../store/hooks';
import { selectAllPosts, selectPerksLoaded, selectPostsLoading } from '../../store/perks.selectors';
import { usePerksConfig } from '../perks/perks-config.context';
import { PerksPostComponent } from '../perks-posts/perks-post/perks-post.component';

export const PerksJoinComponent = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [, setLocation] = useLocation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const perksLoaded = useAppSelector(selectPerksLoaded);
  const loading: boolean =
    useAppSelector(selectPostsLoading) ||
    useAppSelector(selectMembershipsLoading) ||
    useAppSelector(selectAccountSettingsLoading);
  const posts = useAppSelector(selectAllPosts);
  const memberships = useAppSelector(selectMemberships);
  const accountSettingsResp: AccountSettingsResponse = useAppSelector(selectAccountSettings);
  const { accountSettings } = accountSettingsResp || {};
  const { t } = useTranslation();
  const {
    titleSize,
    internalTitle,
    description,
    columns,
    columnAlignment,
    showProgramName,
    showTierImage,
    showTierName,
    showTierDescription,
    showQuickAdd,
    showRecentPosts,
    cardBackgroundColor,
    cardTextColor,
    cardButtonColor,
    cardButtonTextColor,
    columnBorderColor,
    columnBorderWidth,
    borderRadius,
  } = usePerksConfig();

  const anyActiveMemberships =
    (memberships?.length ?? 0) > 0 && memberships?.some((m) => m.state === 'PUBLISHED');

  useEffect(() => {
    if (!isAuthenticated || !perksLoaded || !Array.isArray(memberships)) return;

    const anyActiveMemberships =
      (memberships?.length ?? 0) > 0 && memberships?.some((m) => m.state === 'PUBLISHED');
    const noMembershipsWithContract = memberships?.every((m) => !m.contractId);

    // Stay on /join if user has active memberships but no contract (needs to subscribe)
    if (anyActiveMemberships && noMembershipsWithContract) return;

    // Otherwise redirect to home and let PerksHomeComponent route correctly
    setLocation('/');
  }, [isAuthenticated, perksLoaded, memberships]);

  const openLoginModal = async () => {
    await authenticationFlowService.initiateAuthentication({
      state: LegacyAuthModalState.LOGIN,
      allowedStates: [LegacyAuthModalState.LOGIN, LegacyAuthModalState.RESET_PASSWORD],
    });
  };

  const handleJoinClicked = () => {
    if (containerRef.current) {
      const element = containerRef.current.querySelector('#membership-mgmt');
      if (element) {
        const elementTop = (element as HTMLElement).offsetTop;
        window.scrollTo({
          behavior: 'smooth',
          top: elementTop - 90,
        });
        return;
      }
    }
    // Fallback to scrolling to top if element not found
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div class="single-perks-join-container" ref={containerRef}>
      {loading && (
        <section className="single-loading">
          <SpinnerComponent />
        </section>
      )}

      {!loading && (
        <div id="membership-mgmt">
          <MembershipManagement
            title={internalTitle}
            titleSize={titleSize ?? 'h1'}
            description={description}
            columns={columns ?? '3'}
            columnAlignment={columnAlignment ?? 'left'}
            maxWidth={false}
            showBlock="always"
            showLogin={false}
            showProgramName={showProgramName ?? false}
            showTierImage={showTierImage ?? true}
            showTierName={showTierName ?? true}
            showTierDescription={showTierDescription ?? true}
            showPrice={true}
            showBanner={isAuthenticated}
            showBannerImage={false}
            showQuickAdd={showQuickAdd ?? true}
            redirectToCurrentUrl={true}
            columnBackground={cardBackgroundColor}
            columnTextColor={cardTextColor}
            buttonColor={cardButtonColor}
            buttonTextColor={cardButtonTextColor}
            columnBorderColor={columnBorderColor}
            columnBorderWidth={columnBorderWidth}
            columnBorderRadius={borderRadius}
            verticalPadding="0px"
          />

          {!isAuthenticated && (
            <>
              {anyActiveMemberships && (
                <h3 className="single-login-header h3 single-h3">
                  <span class="single-login-header-text">
                    {accountSettings?.alreadyMemberText || t('common.alreadyMember')}
                  </span>{' '}
                  <span onClick={openLoginModal}>{t('auth.logIn')}</span>
                </h3>
              )}

              {showRecentPosts && posts && posts.length > 0 && anyActiveMemberships && (
                <div className="single-posts-container">
                  <h2 className="single-recent-posts-header h2 single-h2">
                    {t('perks.recentPosts')}
                  </h2>
                  <div className="single-posts">
                    {posts.slice(0, 3).map((post) => (
                      <PerksPostComponent
                        key={post.id}
                        post={post}
                        previewMode={true}
                        onJoinPreviewClicked={handleJoinClicked}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
